import CloseIcon from "@mui/icons-material/Close";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { PromptTemplate, PromptTemplateAudience, PromptTemplateCategory, PromptTemplateScope } from "../../../types/prompt-template";
import { EMPTY_LENGTH } from "../constants/numeric";
import usePromptTemplates from "../hooks/usePromptTemplates";

interface AhaLensesProps {
  open: boolean;
  onClose: () => void;
  onSelectQuestion: (question: string) => void;
}

const AUDIENCE_ICONS: Record<PromptTemplateAudience, React.ReactElement> = {
  individual: <PersonIcon />,
  team: <GroupIcon />,
  manager: <SupervisorAccountIcon />
};

const MAX_LIST_HEIGHT = 320;

const getAudienceIcon = (audience: PromptTemplateAudience): React.ReactElement =>
  AUDIENCE_ICONS[audience] ?? <PersonIcon />;

interface TemplateListProps {
  loading: boolean;
  templates: PromptTemplate[];
  onSelectQuestion: (template: PromptTemplate) => void;
}

const TemplateList = ({ loading, templates, onSelectQuestion }: TemplateListProps): React.ReactElement => {
  if (loading) {
    return (
      <Typography sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
        Loading suggested questions...
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {templates.map(template => (
        <ListItem key={template.id} disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => onSelectQuestion(template)}
            sx={{
              borderRadius: 1,
              bgcolor: "#f0f9ff",
              // eslint-disable-next-line @typescript-eslint/naming-convention
              "&:hover": {
                bgcolor: "#e1f5fe"
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {getAudienceIcon(template.metadata.audience)}
            </ListItemIcon>
            <ListItemText primary={template.title} secondary={template.description} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

interface AhaLensesHeaderProps {
  onClose: () => void;
}

const AhaLensesHeader = ({ onClose }: AhaLensesHeaderProps): React.ReactElement => (
  <Box sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    p: 1,
    pl: 2,
    backgroundColor: "#1976d2",
    color: "white"
  }}>
    <Typography variant="subtitle1" fontWeight="medium">
      Aha Lenses
    </Typography>
    <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
      <CloseIcon />
    </IconButton>
  </Box>
);

interface AhaLensesDialogContentProps {
  selectedCategory: PromptTemplateCategory;
  onCategoryChange: (_event: React.SyntheticEvent, newCategory: PromptTemplateCategory) => void;
  loading: boolean;
  filteredTemplates: PromptTemplate[];
  onSelectQuestion: (template: PromptTemplate) => void;
}

const AhaLensesDialogContent = ({
  selectedCategory,
  onCategoryChange,
  loading,
  filteredTemplates,
  onSelectQuestion
}: AhaLensesDialogContentProps): React.ReactElement => (
  <DialogContent sx={{ p: 0, overflow: "hidden" }}>
    <Tabs
      value={selectedCategory}
      onChange={onCategoryChange}
      indicatorColor="primary"
      textColor="primary"
      variant="fullWidth"
      sx={{
        bgcolor: "#f5f5f5",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "& .MuiTab-root": {
          py: 1.5,
          textTransform: "none",
          fontWeight: 500
        }
      }}
    >
      <Tab label="Training" value="training" />
      <Tab label="Performance" value="performance" />
      <Tab label="Motivation" value="motivation" />
      <Tab label="Discovery" value="discovery" />
    </Tabs>

    <Box sx={{ p: 2 }}>
      <Box sx={{ maxHeight: MAX_LIST_HEIGHT, overflow: "auto" }}>
        <TemplateList
          loading={loading}
          templates={filteredTemplates}
          onSelectQuestion={onSelectQuestion}
        />
      </Box>
    </Box>
  </DialogContent>
);

const AhaLenses = ({ open, onClose, onSelectQuestion }: AhaLensesProps): React.ReactElement => {
  const [selectedCategory, setSelectedCategory] = useState<PromptTemplateCategory>("training");
  const [filteredTemplates, setFilteredTemplates] = useState<PromptTemplate[]>([]);

  const { templates, loading } = usePromptTemplates({
    enabledOnly: true,
    autoFetch: true,
    scope: PromptTemplateScope.enlighten_me
  });

  useEffect(() => {
    if (!templates || templates.length === EMPTY_LENGTH) {
      setFilteredTemplates([]);
      return;
    }

    const filtered = templates.filter(template =>
      template.metadata.category === selectedCategory
    );

    setFilteredTemplates(filtered);
  }, [templates, selectedCategory]);

  const handleCategoryChange = (_event: React.SyntheticEvent, newCategory: PromptTemplateCategory): void => {
    setSelectedCategory(newCategory);
  };

  const handleSelectQuestion = (template: PromptTemplate): void => {
    onSelectQuestion(template.prompt);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <AhaLensesHeader onClose={onClose} />

      <AhaLensesDialogContent
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        loading={loading}
        filteredTemplates={filteredTemplates}
        onSelectQuestion={handleSelectQuestion}
      />
    </Dialog>
  );
};

export default AhaLenses;
